import * as React from 'react';
import { ChevronDown, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/shared/components/ui/badge';
import { cn } from '@/core/lib/utils';
import type { AsyncComboboxOption } from './AsyncCombobox.types';

interface AsyncComboboxTriggerSpecificProps {
  isOpen: boolean;
  isMulti: boolean;
  placeholder?: string;
  selectedOption?: AsyncComboboxOption;
  clearable?: boolean;
  onClear?: (e: React.MouseEvent) => void;
  selectedOptions?: AsyncComboboxOption[];
  maxDisplayedBadges?: number;
  onRemoveBadge?: (id: string) => void;
  renderValue?: (option: AsyncComboboxOption) => React.ReactNode;
}

export type AsyncComboboxTriggerProps = AsyncComboboxTriggerSpecificProps &
  Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'value'>;

export const AsyncComboboxTrigger = React.forwardRef<HTMLButtonElement, AsyncComboboxTriggerProps>(
  (
    {
      isOpen,
      isMulti,
      placeholder,
      selectedOption,
      clearable = true,
      onClear,
      selectedOptions = [],
      maxDisplayedBadges = 3,
      onRemoveBadge,
      renderValue,
      className,
      disabled,
      ...rest
    },
    ref,
  ) => {
    const { t } = useTranslation('asyncCombobox');
    const resolvedPlaceholder = placeholder ?? (isMulti ? t('placeholderMulti') : t('placeholder'));

    const visibleBadges = selectedOptions.slice(0, maxDisplayedBadges);
    const overflowCount = selectedOptions.length - maxDisplayedBadges;

    return (
      <button
        ref={ref}
        type="button"
        disabled={disabled}
        data-state={isOpen ? 'open' : 'closed'}
        className={cn(
          'flex w-full h-9 items-center justify-between gap-2 rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:border-ring',
          'disabled:cursor-not-allowed disabled:opacity-50',
          isOpen && 'border-ring ring-2 ring-ring/20',
          className,
        )}
        {...rest}
      >
        {/* Left: selected value / badges / placeholder */}
        <div className="flex-1 flex items-center gap-1.5 min-w-0 flex-wrap">
          {isMulti ? (
            selectedOptions.length === 0 ? (
              <span className="text-muted-foreground truncate">{resolvedPlaceholder}</span>
            ) : (
              <>
                {visibleBadges.map((opt) => (
                  <Badge
                    key={opt.id}
                    variant="secondary"
                    className="gap-1 pr-1 text-xs font-normal"
                  >
                    {opt.color && (
                      <span
                        className="w-1.5 h-1.5 rounded-full shrink-0"
                        style={{ backgroundColor: opt.color }}
                      />
                    )}
                    <span className="max-w-[100px] truncate">{opt.label}</span>
                    {onRemoveBadge && (
                      <span
                        role="button"
                        tabIndex={-1}
                        onClick={(e) => {
                          e.stopPropagation();
                          onRemoveBadge(opt.id);
                        }}
                        className="ml-0.5 rounded-full hover:bg-destructive/20 hover:text-destructive p-0.5 cursor-pointer"
                      >
                        <X className="w-2.5 h-2.5" />
                      </span>
                    )}
                  </Badge>
                ))}
                {overflowCount > 0 && (
                  <Badge variant="outline" className="text-xs font-normal">
                    +{overflowCount}
                  </Badge>
                )}
              </>
            )
          ) : selectedOption ? (
            renderValue ? (
              renderValue(selectedOption)
            ) : (
              <div className="flex items-center gap-2 min-w-0">
                {selectedOption.color && (
                  <span
                    className="w-2 h-2 rounded-full shrink-0"
                    style={{ backgroundColor: selectedOption.color }}
                  />
                )}
                <span className="truncate">{selectedOption.label}</span>
              </div>
            )
          ) : (
            <span className="text-muted-foreground truncate">{resolvedPlaceholder}</span>
          )}
        </div>

        {/* Right: clear button (single) + chevron */}
        <div className="flex items-center gap-1 shrink-0">
          {!isMulti && clearable && selectedOption && onClear && (
            <span
              role="button"
              tabIndex={-1}
              onClick={(e) => {
                e.stopPropagation();
                onClear(e);
              }}
              className="p-0.5 rounded-full text-muted-foreground hover:text-destructive transition-colors"
            >
              <X className="w-3.5 h-3.5" />
            </span>
          )}
          <ChevronDown
            className={cn(
              'w-4 h-4 text-muted-foreground transition-transform duration-200',
              isOpen && 'rotate-180',
            )}
          />
        </div>
      </button>
    );
  },
);

AsyncComboboxTrigger.displayName = 'AsyncComboboxTrigger';
